import gsap from "gsap";
import { SplitText } from "gsap/src/SplitText";
import { ScrollTrigger } from "gsap/src/ScrollTrigger";
import Lenis from "lenis";


gsap.registerPlugin(SplitText, ScrollTrigger);

// Keep a single root GSAP context so we can revert everything on re-init / transitions
let rootCtx = null;

// Cache reduced-motion preference once
const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// Global coarse-pointer / touch detector
const isTouchDevice =
  (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ||
  'ontouchstart' in window ||
  navigator.maxTouchPoints > 0;

// ✅ Conditional Lenis initialization: Disable on Safari or Stage mode
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isStageMode = new URLSearchParams(window.location.search).get('stage') === 'Stage';
const lenis = (isSafari || isStageMode) ? null : new Lenis({
  duration: 1.5,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: true,
  smoothTouch: false
});

// ✅ Export Lenis instance for global access
export { lenis };

// Animation frame loop to sync Lenis with GSAP
function raf(time) {
  if (lenis) lenis.raf(time);
  requestAnimationFrame(raf);
}
requestAnimationFrame(raf);

// ✅ Fix: Ensure Lenis updates when the page loads and lazy images load
let resizeTimeout;

function refreshLenis() {
  clearTimeout(resizeTimeout); // Cancel previous calls
  resizeTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      if (lenis) lenis.resize(); // ✅ Recalculate dimensions smoothly
    });
  }, 3000); // Adjust delay if necessary
}

// ✅ Refresh Lenis when the page fully loads (not on Safari or Stage mode)
if (!isSafari && !isStageMode) {
  window.addEventListener("load", refreshLenis);
}



function createParallaxEffect() {
  // Don't run parallax if in editing/highlight mode
  if (document.body.classList.contains('highlight-editable-fields')) {
    return;
  }

  gsap.utils.toArray('.parallax-img').forEach(container => {
    const img = container.querySelector('img');
    if (!img) return;

    // Avoid re-initialising if your site transitions/re-inits scripts
    if (container.dataset.parallaxInit === '1') return;
    container.dataset.parallaxInit = '1';

    // Minimal style writes
    container.style.position = container.style.position || 'relative';
    container.style.overflow = container.style.overflow || 'hidden';

    gsap.set(img, {
      scale: 1.082,
      transformOrigin: 'center center',
      willChange: prefersReducedMotion ? 'auto' : 'transform'
    });

    gsap.fromTo(
      img,
      { yPercent: -6 },
      {
        yPercent: 6,
        ease: 'none',
        scrollTrigger: {
          trigger: container,
          scrub: prefersReducedMotion ? 1 : 0.3,
          invalidateOnRefresh: true
        }
      }
    );
  });
}



function pinSections() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  gsap.utils.toArray('.pinned').forEach(section => {
    const isPhoneDisabled = section.classList.contains('disable-phone');
    if (isTouchDevice && isPhoneDisabled) return;

    gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: "top top",
        end: "bottom top",
        pin: true,
        pinSpacing: false,
        scrub: true
      }
    });
  });
}

function ParallaxContent() {
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (isTouchDevice) return;

  gsap.utils.toArray('.parallax-content').forEach(el => {
    const speed = el.dataset.speed ? parseFloat(el.dataset.speed) : -20;

    gsap.to(el, {
      yPercent: speed,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true
      }
    });
  });
}



function setupFunkyNav(options = {}) {
  const {
    buttonSelector = '.funky-menu-btn',
    hamburgerSelector = '.hamburger',
    menuSelector = '.funky-nav-menu',
    linkSelector = '.funky-nav-menu a',
    activeClass = 'is-active'
  } = options;

  const menuButton = document.querySelector(buttonSelector);
  const hamburger = document.querySelector(hamburgerSelector);
  const navMenu = document.querySelector(menuSelector);
  const header = document.querySelector('header');

  if (!menuButton || !navMenu || !hamburger) return;
  if (navMenu.dataset.funkyInit === '1') return;
  navMenu.dataset.funkyInit = '1';

  const allLinks = gsap.utils.toArray(document.querySelectorAll(linkSelector));

  // Accessibility
  if (!navMenu.id) navMenu.id = 'funky-nav-menu';
  menuButton.setAttribute('aria-controls', navMenu.id);
  menuButton.setAttribute('aria-expanded', 'false');
  navMenu.setAttribute('role', 'navigation');
  navMenu.setAttribute('aria-label', 'Main menu');

  const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  const DURATION = prefersReduced ? 0 : 0.4;

  let isOpen = false;

  // Initial state
  gsap.set(navMenu, { autoAlpha: 0, pointerEvents: 'none' });

  function open() {
    if (isOpen) return;
    isOpen = true;

    hamburger.classList.add(activeClass);
    header?.classList.add('menu-open');
    menuButton.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    gsap.to(navMenu, {
      autoAlpha: 1,
      duration: DURATION,
      ease: 'power2.out',
      onStart: () => {
        navMenu.style.pointerEvents = 'auto';
      }
    });
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    menuButton.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    gsap.to(navMenu, {
      autoAlpha: 0,
      duration: DURATION,
      ease: 'power2.in',
      onComplete: () => {
        navMenu.style.pointerEvents = 'none';
        hamburger.classList.remove(activeClass);
        header?.classList.remove('menu-open');
      }
    });
  }

  function toggle() {
    isOpen ? close() : open();
  }

  menuButton.addEventListener('click', toggle);
  allLinks.forEach(link => link.addEventListener('click', close));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) close();
  });
}



function linkHoverEffect() {
  document
    .querySelectorAll('.html_content a, footer address a, footer ul a, footer p a, .ani-link')
    .forEach(link => {
      if (link.dataset.hoverInit === '1') return;
      link.dataset.hoverInit = '1';

      // Ensure positioning only once
      if (getComputedStyle(link).position === 'static') {
        link.style.position = 'relative';
      }

      // Prevent duplicate underline spans if scripts re-init
      let underline = link.querySelector(':scope > span.gn-underline');
      if (!underline) {
        underline = document.createElement('span');
        underline.className = 'gn-underline';
        underline.style.cssText = `
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 1px;
          background: currentColor;
          transform: scaleX(0);
          transform-origin: left;
          pointer-events: none;
          will-change: transform;
        `;
        link.appendChild(underline);
      }

      link.addEventListener('mouseenter', () => {
        gsap.killTweensOf(underline);
        gsap.to(link, { opacity: 0.8, duration: 0.2, ease: 'power1.out', overwrite: 'auto' });
        gsap.to(underline, { scaleX: 1, duration: 0.35, ease: 'power2.out', overwrite: 'auto' });
      }, { passive: true });

      link.addEventListener('mouseleave', () => {
        gsap.killTweensOf(underline);
        gsap.to(link, { opacity: 1, duration: 0.3, ease: 'power1.out', overwrite: 'auto' });
        gsap.to(underline, { scaleX: 0, duration: 0.25, ease: 'power1.out', overwrite: 'auto' });
      }, { passive: true });
    });
}

function fadeUpAnimation() {
  const elements = gsap.utils.toArray('.fade-up');
  
  // Group elements by parent container
  const groups = new Map();
  
  elements.forEach(el => {
    const parent = el.parentElement;
    if (!groups.has(parent)) {
      groups.set(parent, []);
    }
    groups.get(parent).push(el);
  });
  
  // Animate each group with stagger when they enter
  groups.forEach((groupElements) => {
    // Check for stagger value: on first element or parent
    const staggerValue = parseFloat(
      groupElements[0].getAttribute('data-stagger') ||
      groupElements[0].parentElement.getAttribute('data-stagger') ||
      0.15
    );
    
    gsap.fromTo(groupElements,
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: 'power2.out',
        stagger: staggerValue,
        scrollTrigger: {
          trigger: groupElements[0],
          start: 'top bottom-=50',
          once: true
        }
      }
    );
  });
}


// ✅ Destroy before page transition
function destroy() {
  // Revert anything created in our root context first
  if (rootCtx) {
    rootCtx.revert();
    rootCtx = null;
  }

  // Then kill any remaining triggers (belt + braces)
  ScrollTrigger.getAll().forEach(trigger => trigger.kill());

  // Revert any per-section GSAP contexts created for horizontal scroll
  document.querySelectorAll('.horizontal-scroll-section').forEach(section => {
    if (section._hScrollCtx) {
      section._hScrollCtx.revert();
      section._hScrollCtx = null;
    }
  });
}

// === Animation Functions ===
function animateAwardTables() {
  const tables = document.querySelectorAll('.award-table');
  let activeImage = null;
  
  // Offset from cursor (adjust to taste)
  const OFFSET_X = 20;
  const OFFSET_Y = 20;

  tables.forEach((el) => {
    const image = el.querySelector('img.award-img');
    if (!image) return;

    gsap.set(image, {
      position: 'fixed',
      left: 0,
      top: 0,
      xPercent: -50,
      yPercent: -50,
      autoAlpha: 0,
      willChange: 'transform, opacity',
      pointerEvents: 'none',
    });

    let setX, setY;
    
    const align = (e) => {
      setX?.(e.clientX + OFFSET_X);
      setY?.(e.clientY + OFFSET_Y);
    };

    const fade = gsap.to(image, {
      autoAlpha: 1,
      duration: 0.15,
      paused: true,
      onReverseComplete() {
        document.removeEventListener('mousemove', align);
      },
    });

    el.addEventListener('mouseenter', (e) => {
      const startX = activeImage && activeImage !== image
        ? gsap.getProperty(activeImage, 'x')
        : e.clientX + OFFSET_X;
      const startY = activeImage && activeImage !== image
        ? gsap.getProperty(activeImage, 'y')
        : e.clientY + OFFSET_Y;

      gsap.set(image, { x: startX, y: startY });
      activeImage = image;

      setX = gsap.quickTo(image, 'x', { duration: 0.25, ease: 'power3.out' });
      setY = gsap.quickTo(image, 'y', { duration: 0.25, ease: 'power3.out' });

      fade.play();
      document.addEventListener('mousemove', align);
      align(e);
    });

    el.addEventListener('mouseleave', () => fade.reverse());
  });
}


function homepageHeaderReveal() {
  const header = document.querySelector('header[data-homepage]');
  if (!header) return;

  let revealed = false;

  // Set initial hidden state
  gsap.set(header, {
    autoAlpha: 0,
    yPercent: -100,
    willChange: prefersReducedMotion ? 'auto' : 'transform'
  });

  const revealHeader = () => {
    if (revealed) return;
    revealed = true;

    gsap.to(header, {
      autoAlpha: 1,
      yPercent: 0,
      duration: prefersReducedMotion ? 0 : 0.6,
      ease: prefersReducedMotion ? 'none' : 'power2.out',
      clearProps: 'willChange'
    });

    // Kill trigger + delayedCall if they still exist
    st?.kill();
    delayed?.kill?.();
  };

  // Reveal once user scrolls a tiny amount (no manual scroll listener)
  const st = ScrollTrigger.create({
    start: 'top -10',
    onEnter: revealHeader,
    onEnterBack: revealHeader
  });

  // Fallback reveal if user doesn't scroll
  const delayed = gsap.delayedCall(2.3, revealHeader);
}



// ============================================
// ✅ SINGLE INITIALIZATION POINT
// ============================================

function initializeAnimations() {
  // If re-initialising (eg page transitions), tear down first
  destroy();

  rootCtx = gsap.context(() => {
    pinSections();
    ParallaxContent();
    createParallaxEffect();
    setupFunkyNav();
    linkHoverEffect();
    fadeUpAnimation();
    animateAwardTables();
    homepageHeaderReveal();
  });
}

// ============================================
// ✅ AUTO-INIT ON EVERY PAGE LOAD (DRY)
// ============================================
document.addEventListener('DOMContentLoaded', initializeAnimations);

// ✅ Export for modular use
export default { initializeAnimations };